/**
 * @fileoverview Modules & Features step of the tenant onboarding wizard —
 * module catalog selection plus retention, map, and notification defaults.
 * @module system-web/pages/tenants/wizard/steps/ModulesFeaturesStep
 *
 * Responsibilities:
 * - Render the module catalog with per-module enable checkboxes
 * - Enforce selection invariants: at least one enabled module, and
 *   deprecated catalog modules cannot be newly enabled
 * - Collect data retention, map default, and notification settings
 *
 * Dependencies:
 * - moduleService.listModules (Dexie-backed catalog)
 * - @firesquirrel/core-domain module status policy helpers
 * - WizardState / StepError contracts from the wizard
 *
 * Architecture:
 * - Controlled wizard step; all changes flow through onChange patches
 * - Module normalization runs via buildNextModules so every catalog code
 *   gets an explicit isEnabled value in wizard state
 *
 * Notes:
 * - Deprecated entries can be disabled but never enabled; the creation-side
 *   contract is enforced again by api tenantModuleResolver at provisioning.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { listModules } from '../../../../services/moduleService';
import { canEnableModuleSelection, canonicalizeTenantModuleCode, isDeprecatedModuleStatus, isModuleEnabledByDefault } from '@firesquirrel/core-domain';
import type { WizardState } from '../types';
import type { StepError } from '../validateStep';

interface Props {
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
  errors?: StepError[];
}

/**
 * @component ModulesFeaturesStep
 * Purpose:     Wizard step rendering the module catalog and feature defaults.
 * Props:       state (WizardState), onChange (patch callback), errors
 * Behavior:    Normalizes wizard modules to cover every catalog code;
 *              deprecated modules render unselectable unless already enabled.
 * Data Source: local (Dexie via moduleService.listModules)
 * Constraints: no direct API calls; deprecated modules never default on
 * Performance: catalog rendered once per load; no virtualization needed
 */
export default function ModulesFeaturesStep({ state, onChange, errors = [] }: Props) {
  const [showRetention, setShowRetention] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [moduleCatalog, setModuleCatalog] = useState<Array<{ code: string; name: string; description?: string; enabledByDefault?: boolean; status?: string }>>([]);
  const [moduleCatalogError, setModuleCatalogError] = useState<string | null>(null);
  const [catalogStatus, setCatalogStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;

    listModules()
      .then((items) => {
        if (cancelled) return;
        const normalized = (items ?? [])
          .filter((item: any) => item && !item._deleted && typeof item.code === 'string' && item.code.trim())
          .map((item: any) => ({
            code: String(item.code).trim(),
            name: String(item.name ?? item.code).trim(),
            description: typeof item.description === 'string' ? item.description.trim() : undefined,
            enabledByDefault: Boolean(item.enabledByDefault),
            status: typeof item.status === 'string' ? item.status : undefined,
          }))
          .sort((a, b) => {
            if (a.code === 'wildlandFire') return -1;
            if (b.code === 'wildlandFire') return 1;
            return (a.name || a.code).localeCompare(b.name || b.code);
          });
        setModuleCatalog(normalized);
        setModuleCatalogError(null);
        setCatalogStatus('loaded');
      })
      .catch((err: any) => {
        if (cancelled) return;
        setModuleCatalog([]);
        setModuleCatalogError(err?.message || 'Failed to load module catalog');
        setCatalogStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const catalogModules = useMemo(() => {
    const withWildland = moduleCatalog.some((module) => module.code === 'wildlandFire')
      ? moduleCatalog
      : [{
        code: 'wildlandFire',
        name: 'Wildland Fire',
        description: 'Default module for every department.',
        enabledByDefault: true,
        status: 'ga',
      }, ...moduleCatalog];
    return withWildland;
  }, [moduleCatalog]);

  const normalizedCurrentModules = useMemo(() => (
    state.modules
      .filter((module) => module && typeof module.code === 'string' && module.code.trim())
      .map((module) => ({
        code: String(module.code).trim(),
        isEnabled: module.isEnabled !== false,
      }))
  ), [state.modules]);

  // Draft/tenant state may store drifted kebab spellings (e.g. 'fire-code',
  // 'wildland'); key the enabled map by canonical code so those selections
  // still match the canonical camelCase catalog rows.
  const normalizedCurrentMap = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const module of normalizedCurrentModules) {
      const key = canonicalizeTenantModuleCode(module.code);
      if (module.isEnabled || !map.has(key)) map.set(key, module.isEnabled);
    }
    return map;
  }, [normalizedCurrentModules]);

  // Enabled codes that match no catalog entry even after canonicalization —
  // dead drifted spellings that can never pass server validation. Only
  // computed once the catalog has loaded so pending loads never flag saved
  // draft selections as removed.
  const droppedEnabledModuleCodes = useMemo(() => {
    if (catalogStatus !== 'loaded') return [];
    const catalogCodeSet = new Set(catalogModules.map((m) => canonicalizeTenantModuleCode(m.code)));
    return normalizedCurrentModules
      .filter((m) => m.isEnabled && !catalogCodeSet.has(canonicalizeTenantModuleCode(m.code)))
      .map((m) => m.code);
  }, [catalogModules, catalogStatus, normalizedCurrentModules]);

  const isCatalogModuleEnabled = (
    module: { code: string; enabledByDefault?: boolean; status?: string },
    currentMap = normalizedCurrentMap,
  ): boolean => {
    const key = canonicalizeTenantModuleCode(module.code);
    return currentMap.has(key)
      ? currentMap.get(key) === true
      : isModuleEnabledByDefault(module);
  };

  // Codes absent from a successfully loaded catalog are dropped rather than
  // preserved — a module the catalog doesn't know can never validate
  // server-side. When the catalog fails to load, preserve existing draft
  // selections so saved work is not lost.
  const buildNextModules = (currentMap = normalizedCurrentMap) => {
    const next: Array<{ code: string; isEnabled: boolean }> = [];
    const seenCanonical = new Set<string>();

    for (const module of catalogModules) {
      const canonical = canonicalizeTenantModuleCode(module.code);
      const isEnabled = isCatalogModuleEnabled(module, currentMap);
      next.push({ code: module.code, isEnabled });
      seenCanonical.add(canonical);
    }

    if (catalogStatus !== 'loaded') {
      for (const module of normalizedCurrentModules) {
        const canonical = canonicalizeTenantModuleCode(module.code);
        if (seenCanonical.has(canonical)) continue;
        next.push({ code: module.code, isEnabled: module.isEnabled });
        seenCanonical.add(canonical);
      }
    }

    // Guarantee at least one catalog module remains enabled if dropping
    // obsolete codes left every catalog module disabled.
    if (next.length > 0 && !next.some((m) => m.isEnabled)) {
      const fallbackIdx = next.findIndex((m) =>
        canEnableModuleSelection(catalogModules.find((cm) => cm.code === m.code)),
      );
      const idx = fallbackIdx >= 0 ? fallbackIdx : 0;
      next[idx] = { ...next[idx], isEnabled: true };
    }

    return next;
  };

  useEffect(() => {
    // Do not normalize draft selections against the synthetic fallback while
    // listModules() is still in flight — doing so would wipe saved non-Wildland
    // selections before the catalog arrives.
    if (catalogStatus === 'loading') return;
    const nextModules = buildNextModules();
    const currentJson = JSON.stringify(normalizedCurrentModules);
    const nextJson = JSON.stringify(nextModules);
    if (currentJson !== nextJson) {
      onChange({ modules: nextModules });
    }
  }, [buildNextModules, catalogStatus, normalizedCurrentModules, onChange]);

  const handleModuleToggle = (code: string, enabled: boolean) => {
    // Deprecated modules cannot be newly enabled; disabling an enabled
    // deprecated module stays allowed so it can still be turned off.
    if (enabled && !canEnableModuleSelection(catalogModules.find((m) => m.code === code))) {
      return;
    }
    const targetCanonical = canonicalizeTenantModuleCode(code);
    if (!enabled) {
      const otherCatalogEnabled = catalogModules.some(
        (m) => canonicalizeTenantModuleCode(m.code) !== targetCanonical && isCatalogModuleEnabled(m, normalizedCurrentMap),
      );
      if (!otherCatalogEnabled) {
        return;
      }
    }
    const nextMap = new Map(normalizedCurrentMap);
    nextMap.set(targetCanonical, enabled);
    onChange({ modules: buildNextModules(nextMap) });
  };

  const dr = state.dataRetentionPolicy;
  const md = state.mapDefaults;

  const updateDR = (patch: Record<string, any>) => onChange({ dataRetentionPolicy: { ...dr, ...patch } });
  const updateMap = (patch: Record<string, any>) => onChange({ mapDefaults: { ...md, ...patch } });

  const CollapsibleSection = ({ title, open, toggle, children }: { title: string; open: boolean; toggle: () => void; children: React.ReactNode }) => (
    <div className="border border-gray-200 rounded-lg">
      <button type="button" onClick={toggle} className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50">
        {title}
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Modules */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Modules</h3>
        <p className="text-xs text-gray-500 mb-3">
          Wildland is the default module. At least one module must be selected — the last enabled module cannot be unselected.
        </p>
        {moduleCatalogError && (
          <p className="text-xs text-amber-600 mb-3">{moduleCatalogError}</p>
        )}
        {droppedEnabledModuleCodes.length > 0 && (
          <p className="text-xs text-amber-600 mb-3">
            Removed unavailable module selection(s): {droppedEnabledModuleCodes.join(', ')} — no longer in the module catalog.
          </p>
        )}
        <div className="grid grid-cols-1 gap-3">
          {catalogModules.map((module) => {
            const moduleCanonical = canonicalizeTenantModuleCode(module.code);
            const checked = isCatalogModuleEnabled(module, normalizedCurrentMap);
            const otherModulesEnabled = catalogModules.some(
              (m) =>
                canonicalizeTenantModuleCode(m.code) !== moduleCanonical
                && isCatalogModuleEnabled(m, normalizedCurrentMap),
            );
            // Whichever module is the sole enabled one cannot be unselected,
            // so the tenant never ends up with zero modules.
            // A deprecated module cannot be newly selected, but an enabled
            // deprecated module can still be unselected.
            const disabled =
              (checked && !otherModulesEnabled) ||
              (!checked && !canEnableModuleSelection(module));
            return (
              <label
                key={module.code}
                className={`flex items-start gap-3 p-3 rounded-lg border ${disabled ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white'}`}
                title={!checked && isDeprecatedModuleStatus(module.status)
                  ? 'Module is deprecated — cannot be enabled'
                  : checked && !otherModulesEnabled
                    ? 'At least one module must stay enabled'
                    : undefined}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={(e) => handleModuleToggle(module.code, e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 disabled:cursor-not-allowed"
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    <span>{module.name}</span>
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                      {module.code}
                    </span>
                    {module.status && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                        {module.status}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {module.description || (module.code === 'wildlandFire' ? 'Default module for every department.' : 'Enable this module for the tenant.')}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </section>

      {/* Collapsible sections */}
      <CollapsibleSection title="Data Retention Policy" open={showRetention} toggle={() => setShowRetention(!showRetention)}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { key: 'inspectionRetentionYears', label: 'Inspections', def: 7 },
            { key: 'photoRetentionYears', label: 'Photos', def: 7 },
            { key: 'documentRetentionYears', label: 'Documents', def: 10 },
            { key: 'reportRetentionYears', label: 'Reports', def: 10 },
            { key: 'auditLogRetentionYears', label: 'Audit Logs', def: 7 },
          ].map(({ key, label, def }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{label} (years)</label>
              <input type="number" min="1" max="99" value={(dr as any)[key] ?? def} onChange={(e) => updateDR({ [key]: parseInt(e.target.value) || def })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Deletion Policy</label>
            <select value={dr.deletionPolicy || 'archive'} onChange={(e) => updateDR({ deletionPolicy: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="archive">Archive</option>
              <option value="purge">Purge</option>
            </select>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Notification Preferences" open={showNotifications} toggle={() => setShowNotifications(!showNotifications)}>
        <p className="text-xs text-gray-500 mb-3">Default notification settings. Can be customized after department creation.</p>
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-gray-300 text-orange-600" />
            <span className="text-sm text-gray-700">Notify inspector on assignment</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-gray-300 text-orange-600" />
            <span className="text-sm text-gray-700">Notify admin on inspection completion</span>
          </label>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Map Defaults" open={showMap} toggle={() => setShowMap(!showMap)}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Default Zoom</label>
            <input type="number" min="1" max="20" value={md.defaultZoom ?? 12} onChange={(e) => updateMap({ defaultZoom: parseInt(e.target.value) || 12 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Map Style</label>
            <select value={md.mapStyle || 'streets'} onChange={(e) => updateMap({ mapStyle: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="streets">Streets</option>
              <option value="satellite">Satellite</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
}
